import type { CrudFilters } from "@refinedev/core";

import { isEnrichmentContentType } from "@/lib/tina/content-types";
import { ENRICHMENT_CLASS_SENTINEL } from "@/lib/tina/enrichment-jobs";

/** Sentinel value for the "Cron" option in the triggerer filter. */
export const CRON_TRIGGERER = "__cron__";

/**
 * True when the selected content type puts the list in the enrichment class —
 * either one of the nine flows, or the sentinel that means all nine.
 *
 * This is what decides the column variant and which toolbar controls make
 * sense, so it has to accept both opt-ins Tina supports; testing only the nine
 * would render job rows with scrape columns whenever "All enrichment jobs" is
 * picked.
 */
export const isEnrichmentView = (contentType: string | null): boolean =>
  contentType === ENRICHMENT_CLASS_SENTINEL ||
  isEnrichmentContentType(contentType);

/**
 * True when a listing is scoped to `status=hitl`, and therefore reaches HITL
 * rows of every class Tina returns — refresh included.
 *
 * That inclusion is the *problem* this predicate now marks, not a feature.
 * `list_events` hides refresh-class rows from a plain `GET /events`, and
 * ruling R34 made `status=hitl` the one exception; the owner has since ruled
 * that refresh reviews belong on their own surface
 * (`/tina/refresh-review`), so the queue has to take the exception back out.
 * `ItemsList` uses this to drop refresh rows from the page it renders.
 *
 * Read off the caller's `initialFilters` rather than taken as a prop, because
 * that IS the reason: a preset that stops pinning `status=hitl` cannot receive
 * refresh rows in the first place and stops paying for the filter in the same
 * edit, instead of drifting into hiding rows a listing never had.
 *
 * Only a genuinely constant `initialFilters` entry counts. The user-controlled
 * filters `buildItemsFilters` appends are all `value: undefined` when cleared,
 * and none of them is `status`.
 */
export const listsHitlRows = (initialFilters?: CrudFilters): boolean =>
  (initialFilters ?? []).some(
    (filter) =>
      "field" in filter &&
      filter.field === "status" &&
      filter.operator === "eq" &&
      filter.value === "hitl",
  );

export interface ItemsFilterState {
  /** Genuinely-constant filters supplied by the caller (e.g. status=hitl). */
  initialFilters?: CrudFilters;
  country: string | null;
  runId: string | null;
  /** A member uid, the CRON_TRIGGERER sentinel, or null (any). */
  triggeredBy: string | null;
  showSeeds: boolean;
  /**
   * One `content_type` value, or null for the server default.
   *
   * **There is no `job_class` param.** Tina solves the same problem through
   * `content_type`: `list_events` appends `scrape_class_only()` as the `else`
   * branch of its `content_type` filter, so enrichment rows are hidden unless a
   * caller opts in. That default is correct and stays.
   *
   * Three values are meaningful, and the third is why a curator no longer picks
   * flows one at a time:
   *
   * - `null` — send nothing, i.e. Tina's scrape-class default;
   * - `enrichment_<entity>` — one of the nine flows, an exact match;
   * - `enrichment` — {@link ENRICHMENT_CLASS_SENTINEL}, every job whatever its
   *   entity. Not a `content_type` any row carries: `list_events` special-cases
   *   the bare word to `enrichment_class_only()` before the equality branch, so
   *   it is a third explicit opt-in beside `content_type=` and `entity_id=`.
   */
  contentType: string | null;
}

/**
 * Build the HITL queue's `permanent` filter set for Refine's `useTable`.
 *
 * Every user-controlled filter is ALWAYS emitted as an entry, using
 * `value: undefined` when cleared — never omitted. This is load-bearing, not
 * cosmetic:
 *
 * Refine seeds `permanent` filters into `useTable`'s internal mutable filter
 * state (`setInitialFilters`), and the query is built as
 * `unionFilters(permanent, internalState)` — a union deduped by
 * (field, operator) where `permanent` wins, after which undefined/null-valued
 * entries are dropped. If a cleared filter is simply omitted from `permanent`,
 * a stale copy left in the internal state (seeded while it was set, especially
 * under `syncWithLocation`) is no longer shadowed and survives — the queue
 * stays stuck on the previously chosen run/country until a refresh remounts and
 * re-seeds. Keeping the field present with `value: undefined` shadows the stale
 * internal copy and is then dropped, so clearing actually clears.
 */
export const buildItemsFilters = ({
  initialFilters,
  country,
  runId,
  triggeredBy,
  showSeeds,
  contentType,
}: ItemsFilterState): CrudFilters =>
  [
    ...(initialFilters ?? []),
    { field: "content_type", operator: "eq", value: contentType ?? undefined },
    { field: "country", operator: "eq", value: country ?? undefined },
    { field: "run_id", operator: "eq", value: runId ?? undefined },
    {
      field: "triggered_by",
      operator: "eq",
      value:
        triggeredBy && triggeredBy !== CRON_TRIGGERER ? triggeredBy : undefined,
    },
    {
      field: "trigger_type",
      operator: "eq",
      value: triggeredBy === CRON_TRIGGERER ? "cron" : undefined,
    },
    // KNOWN NO-OP, and long-standing: `GET /events` has never accepted
    // `exclude_seed` (`grep -rn exclude_seed` over Tina's `src/` finds nothing),
    // and FastAPI ignores an unknown query param rather than rejecting it — so
    // the "Show discover-seeds" toggle has always been inert and seeds are
    // always shown. Left emitted rather than silently dropped: the fix is a Tina
    // param, and a client-side filter would desync the server-computed `total`
    // from the rows on screen. Tracked as a follow-up.
    {
      field: "exclude_seed",
      operator: "eq",
      value: showSeeds ? undefined : true,
    },
  ] as CrudFilters;
